'use client';

import { useEffect, useState } from 'react';
import Lottie from 'react-lottie-player';
import { useRouter } from 'next/navigation';
import { ErrorOutline } from '@mui/icons-material';
import { Button, IconButton, Stack, Typography } from '@mui/material';
import { isFalsy } from 'utility-types';
import movingDots from '@/lotties/dot_indicator.json';
import { getPosts, getPostsforStatistics } from '@/api/appstore.api';
import { refreshAccessToken } from '@/api/auth.api';
import { checkTeam } from '@/api/stores.api';
import type { PostFilterType } from '@/interfaces/component/filter.interface';
import type { ServiceGuideModalType } from '@/interfaces/component/modal.interface';
import { RequireAuthType } from '@/interfaces/component/requireAuth.interface';
import type { AppStore } from '@/interfaces/service/appstore/appstore.interface';
import { useAppStoreStore } from '@/stores/appstore.store';
import { useOrganizationTeamStore } from '@/stores/organizationTeam.store';
import { useToastStore } from '@/stores/toast.store';
import { setRequestPostParams } from '@/services/appstore.service';
import { filtersMap } from '@/constants/appstore/post.filter.constnats';
import { REVIEW_ROUTES, ROUTES } from '@/constants/path.constants';
import { getToken } from '@/utils/auth.util';
import { getOranizationIdTeamId } from '@/utils/organizationTeam.util';
import FlexBox from '@/components/FlexBox';
import RequestAuthenticationModal from '@/components/modal/RequestAuthenticationModal';
import ServiceGuideModalResolver from '@/views/appstore/dashboard/components/ServiceGuideModalResolver';
import AlreadyConfirmedModal from '@/views/organization/components/AlreadyConfirmedModal';
import CreateOrganizationModal from '@/views/organization/components/CreateOrganizationModal';
import OwnershipVerificationModal from '@/views/organization/components/OwnershipVerificationModal';
import TeamRegistrationModal from '@/views/organization/components/TeamRegistrationModal';
import theme from '@/app/theme';
import arrowAnimation from '@/styles/arrow.animation.module.css';

const PostGroupButton = ({
  label,
  onLabelClick,
  onClick,
  textColor,
  counts = 0,
  isMobile,
  description = '',
  isStable,
  showAuthButton = false,
  onAuthClick
}: {
  label: string;
  onLabelClick?: () => void;
  onClick: () => void;
  textColor: string;
  counts?: number;
  isMobile: boolean;
  description?: string;
  isStable: boolean;
  showAuthButton?: boolean;
  onAuthClick?: () => void;
}) => {
  return (
    <Stack
      className={arrowAnimation['post-group-button']}
      sx={{
        width: '100%',
        padding: isMobile ? '40px 20px' : '24px',
        borderRadius: '8px',
        backgroundColor: 'white',
        cursor: 'pointer'
      }}
      onClick={() => onClick()}
    >
      <Stack direction={'row'} gap={'4px'}>
        <Typography variant="subtitle1" color={textColor} fontWeight={600}>
          {label}
        </Typography>
        {onLabelClick ? (
          <IconButton
            sx={{ padding: 0 }}
            onClick={(e) => {
              e.stopPropagation();
              onLabelClick();
            }}
          >
            <ErrorOutline sx={{ width: '16px', height: '16px', color: textColor }} />
          </IconButton>
        ) : (
          ''
        )}
      </Stack>
      <Typography variant="subtitle2" fontWeight={400} display="flex" gap="8px" flex={1}>
        {isStable || (!isStable && counts > 0) ? (
          description
        ) : (
          <FlexBox style={{ flex: 1, gap: '8px' }}>
            <Lottie loop animationData={movingDots} play style={{ width: '24px', height: 'auto' }} />
            {label}를 확인하는 중입니다.
          </FlexBox>
        )}
      </Typography>
      <FlexBox style={{ justifyContent: 'start', marginTop: '10px', gap: '8px' }}>
        {showAuthButton ? (
          <Button
            variant="contained"
            size="large"
            onClick={(e) => {
              e.stopPropagation();
              if (onAuthClick) {
                onAuthClick();
              }
            }}
            sx={{
              width: '100%',
              height: '56px',
              borderRadius: '8px',
              textTransform: 'none',
              fontWeight: 600,
              boxShadow: 'none',
              mt: 1
            }}
          >
            내 병원 인증하기
          </Button>
        ) : (
          <>
            <Typography variant={isMobile ? 'h2' : 'h1'}>{isStable || (!isStable && counts > 0) ? `${counts}건` : ''}</Typography>
            {onLabelClick && (isStable || (!isStable && counts > 0)) ? (
              <FlexBox>
                {[1, 2, 3].map((_, i) => (
                  <svg className={arrowAnimation.arrow} key={i} xmlns="http://www.w3.org/2000/svg" width="20" height="24" viewBox="0 0 8 14" fill="none">
                    <path
                      d="M0.451987 1.57999L1.51299 0.519991L7.29199 6.29699C7.38514 6.38956 7.45907 6.49963 7.50952 6.62088C7.55997 6.74213 7.58594 6.87216 7.58594 7.00349C7.58594 7.13482
                        7.55997 7.26485 7.50952 7.3861C7.45907 7.50735 7.38514 7.61742 7.29199 7.70999L1.51299 13.49L0.452987 12.43L5.87699 7.00499L0.451987 1.57999Z"
                      fill="black"
                    />
                  </svg>
                ))}
              </FlexBox>
            ) : (
              ''
            )}
          </>
        )}
      </FlexBox>
    </Stack>
  );
};

const PostGroupButtons = ({ currentAppStore, isMobile, isStable }: { currentAppStore: AppStore; isMobile: boolean; isStable: boolean; teamId?: string }) => {
  const router = useRouter();
  const { matchedTeamId } = getOranizationIdTeamId();
  const { teams, organizations } = useOrganizationTeamStore();
  const [showModal, setShowModal] = useState(false);
  const [modalKey, setModalKey] = useState<ServiceGuideModalType | undefined>();
  const { setShowToast, setToastInfos } = useToastStore();

  // 내 병원인지 확인
  const isMyStore = currentAppStore ? teams.some((team) => Number(team.storeId) === currentAppStore.storeId) : false;

  // 인증 관련 상태
  const [loading, setLoading] = useState(false);
  const [showOwnershipVerificationModal, setShowOwnershipVerificationModal] = useState(false);
  const [showCreateOrganizationModal, setShowCreateOrganizationModal] = useState(false);
  const [showTeamRegistrationModal, setShowTeamRegistrationModal] = useState(false);
  const [showAlreadyConfirmedModal, setShowAlreadyConfirmedModal] = useState(false);
  const [showRequestAuthenticationModal, setShowRequestAuthenticationModal] = useState(false);
  const [teamAuthId, setTeamAuthId] = useState<string>('');
  const [currentOrganizationId, setCurrentOrganizationId] = useState<string>('');
  const [currentOrganizationName, setCurrentOrganizationName] = useState<string>('');

  const { NEGATIVE_POSTS_NO_ACTION, NO_REPLY, NEGATIVE_POSTS_ACTION_PROGRESS } = filtersMap;

  const [isLoadded, setIsLoadded] = useState(false);

  const { setAppStorePostsFilters } = useAppStoreStore((state) => state);

  const [counts, setCounts] = useState<Record<'NEGATIVE_POSTS_NO_ACTION' | 'NO_REPLY' | 'NEGATIVE_POSTS_ACTION_PROGRESS', number | undefined>>({
    NEGATIVE_POSTS_NO_ACTION: undefined,
    NO_REPLY: undefined,
    NEGATIVE_POSTS_ACTION_PROGRESS: undefined
  });

  // 인증 관련 함수
  const registerTeam = async () => {
    if (!currentAppStore) return;

    const token = getToken();
    if (!token) {
      setShowRequestAuthenticationModal(true);
      return;
    }

    setLoading(true);
    try {
      // 팀 정보 확인
      const teamCheckResult = await checkTeam(currentAppStore.storeId);

      if (!teamCheckResult.isSuccess || !teamCheckResult.data) {
        setToastInfos({ text: teamCheckResult.message || '팀 정보 확인에 실패했습니다.' });
        setShowToast(true);
        return;
      }

      if (teamCheckResult.data.isAlreay) {
        // 이미 다른 조직에 속해 있으면 모달 표시
        setShowAlreadyConfirmedModal(true);
      } else {
        // 소유권 확인 모달 표시
        setShowOwnershipVerificationModal(true);
      }
    } catch (error) {
      console.error('팀 확인 중 오류 발생:', error);
      setToastInfos({ text: '팀 확인 중 오류가 발생했습니다.' });
      setShowToast(true);
    } finally {
      setLoading(false);
    }
  };

  const handleCloseOwnershipVerificationModal = () => {
    setShowOwnershipVerificationModal(false);
  };

  const handleOwnershipVerificationSuccess = async (authId: string) => {
    handleCloseOwnershipVerificationModal();
    setTeamAuthId(authId);

    setLoading(true);
    try {
      if (organizations && organizations.length > 0 && organizations[0].organizationId) {
        // 이미 조직이 있으면 해당 조직 정보 설정
        setCurrentOrganizationId(organizations[0].organizationId);
        setCurrentOrganizationName(organizations[0].organizationName);

        // 바로 팀 등록 모달로 진행
        setShowTeamRegistrationModal(true);
      } else {
        // 조직이 없으면 조직 생성 모달 표시
        setShowCreateOrganizationModal(true);
      }
    } catch (error) {
      console.error('조직 확인 중 오류 발생:', error);
      setToastInfos({ text: '조직 확인 중 오류가 발생했습니다.' });
      setShowToast(true);
    } finally {
      setLoading(false);
    }
  };

  const handleCloseCreateOrganizationModal = () => {
    setShowCreateOrganizationModal(false);
  };

  const handleCreateOrganizationSuccess = async (orgData: { organizationId: string; organizationName: string }) => {
    handleCloseCreateOrganizationModal();

    // 조직 정보 설정
    setCurrentOrganizationId(orgData.organizationId);
    setCurrentOrganizationName(orgData.organizationName);

    // 토큰 갱신
    await refreshAccessToken();

    // 조직 생성 후 팀 등록 모달로 진행
    setShowTeamRegistrationModal(true);
  };

  const handleCloseTeamRegistrationModal = () => {
    setShowTeamRegistrationModal(false);
  };

  const handleTeamRegistrationSuccess = async (teamName: string) => {
    handleCloseTeamRegistrationModal();

    // 토큰 갱신
    await refreshAccessToken();

    // 페이지 새로고침 또는 필요한 작업 수행
    window.location.reload();
  };

  const handleCloseAlreadyConfirmedModal = () => {
    setShowAlreadyConfirmedModal(false);
  };

  const handleCloseRequestAuthenticationModal = () => {
    setShowRequestAuthenticationModal(false);
  };

  const groupProps: Record<'NEGATIVE_POSTS_NO_ACTION' | 'NO_REPLY' | 'NEGATIVE_POSTS_ACTION_PROGRESS', { textColor: string; description: string; label?: string }> = {
    NEGATIVE_POSTS_NO_ACTION: {
      textColor: theme.palette.error.dark,
      description: '답변 작성 또는 삭제가 필요한 리뷰를 확인하세요.'
    },
    NO_REPLY: {
      label: '최근 3개월 내 미답변 리뷰',
      textColor: theme.palette.warning.dark,
      description: '최근 3개월 내 답변이 필요한 리뷰를 확인하세요.'
    },
    NEGATIVE_POSTS_ACTION_PROGRESS: {
      textColor: theme.palette.text.primary,
      description: '삭제 또는 엔터프라이즈 관리 요청 해 주신 내역입니다.'
    }
  };

  const threeMonthsAgo = new Date();
  threeMonthsAgo.setMonth(threeMonthsAgo.getMonth() - 3);
  const startAt = Math.floor(threeMonthsAgo.getTime() / 1000);

  const NO_REPLY_WITHIN_RECENT_MONTHS = {
    ...NO_REPLY,
    option: {
      ...NO_REPLY.option,
      startAt
    }
  };

  const postFilterGroupsForCounts = [NEGATIVE_POSTS_NO_ACTION, NO_REPLY_WITHIN_RECENT_MONTHS, NEGATIVE_POSTS_ACTION_PROGRESS];

  const postFilterGroupsForDisplay = [NEGATIVE_POSTS_NO_ACTION, NO_REPLY, NEGATIVE_POSTS_ACTION_PROGRESS];

  const openModal = (type: PostFilterType) => {
    switch (type) {
      case 'NEGATIVE_POSTS_NO_ACTION':
        setModalKey('negativePostsNoAction');
        break;
      case 'NO_REPLY':
        setModalKey('noReply');
        break;
      case 'NEGATIVE_POSTS_ACTION_PROGRESS':
        setModalKey('negativePostsActionProgress');
        break;
      default:
        break;
    }
    setShowModal(true);
  };

  const goToReviewsPage = () => router.push(REVIEW_ROUTES.getReviews(currentAppStore.storeId));

  const getPostsCounts = async () => {
    Promise.allSettled(postFilterGroupsForCounts.map(({ option }) => getPosts(setRequestPostParams({ ...currentAppStore, filter: option }), matchedTeamId ? String(matchedTeamId) : undefined))).then(
      (response) => {
        let _counts = counts;

        response?.forEach((res, i) => {
          if (res?.status === 'fulfilled' && res?.value) {
            const group = postFilterGroupsForCounts[i];

            _counts = {
              ..._counts,
              [group.value]: res.value.totalCount ?? 0
            };
          }
        });
        setCounts(_counts);
        setIsLoadded(true);
      }
    );
  };

  useEffect(() => {
    if (!isLoadded) {
      getPostsCounts();
    }
  }, [isLoadded]);

  return (
    <>
      <Stack direction={isMobile ? 'column' : 'row'} rowGap={'10px'} columnGap={'16px'}>
        {postFilterGroupsForDisplay.map(({ label, option, value, memberOnly }) => {
          if (memberOnly) {
            const token = getToken();
            if (isFalsy(token)) {
              return '';
            }
          }

          // matchedTeamId 대신 isMyStore 사용
          const showAuthButton = value === 'NEGATIVE_POSTS_ACTION_PROGRESS' && !isMyStore;

          return (
            <PostGroupButton
              key={value}
              label={label}
              onLabelClick={() => openModal(value)}
              onClick={() => {
                setAppStorePostsFilters(currentAppStore.storeId, option);
                goToReviewsPage();
              }}
              counts={counts[value]}
              isMobile={isMobile}
              isStable={isStable}
              showAuthButton={showAuthButton}
              onAuthClick={registerTeam}
              {...groupProps[value]}
            />
          );
        })}

        <ServiceGuideModalResolver
          modalKey={modalKey}
          showModal={showModal}
          handleShowModal={() => {
            setShowModal(!showModal);
            if (showModal) {
              setModalKey(undefined);
            }
          }}
        />
      </Stack>

      {/* 소유권 확인 모달 */}
      {currentAppStore && (
        <OwnershipVerificationModal
          showModal={showOwnershipVerificationModal}
          handleShowModal={handleCloseOwnershipVerificationModal}
          storeName={currentAppStore.storeName}
          storeId={currentAppStore.storeId}
          onLoginSuccess={handleOwnershipVerificationSuccess}
        />
      )}

      {/* 조직 생성 모달 */}
      {currentAppStore && (
        <CreateOrganizationModal
          teamAuthId={teamAuthId}
          storeId={currentAppStore.storeId}
          storeName={currentAppStore.storeName}
          showModal={showCreateOrganizationModal}
          handleShowModal={handleCloseCreateOrganizationModal}
          onOrganizationCreated={handleCreateOrganizationSuccess}
        />
      )}

      {/* 팀 등록 모달 */}
      {currentAppStore && (
        <TeamRegistrationModal
          showModal={showTeamRegistrationModal}
          handleShowModal={handleCloseTeamRegistrationModal}
          storeName={currentAppStore.storeName}
          storeId={currentAppStore.storeId}
          teamAuthId={teamAuthId}
          organizationId={currentOrganizationId}
          organizationName={currentOrganizationName}
          onRegisterComplete={handleTeamRegistrationSuccess}
        />
      )}

      {/* 이미 인증된 병원 모달 */}
      {currentAppStore && <AlreadyConfirmedModal showModal={showAlreadyConfirmedModal} handleShowModal={handleCloseAlreadyConfirmedModal} storeName={currentAppStore.storeName} />}

      {/* 로그인 요청 모달 */}
      <RequestAuthenticationModal showModal={showRequestAuthenticationModal} handleShowModal={handleCloseRequestAuthenticationModal} type={RequireAuthType.AI_RECOMMEND_REPLY} />
    </>
  );
};

export default PostGroupButtons;
